import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { 
  FiFilter, FiSearch, FiAlertTriangle, FiLoader, 
  FiCheck, FiRefreshCw, FiEye, FiX, FiMessageSquare, 
  FiTruck, FiMoreVertical, FiCheckCircle
} from 'react-icons/fi';
import { ClaimDrawer } from '../components/claims/ClaimDrawer';
import supabase from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';

export const ExceptionsQueue = () => {
  const { profile } = useAuth();
  const [exceptions, setExceptions] = useState([]);
  const [salvagersList, setSalvagersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Drawer & Context Menu
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  // Action Modal State
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionClaim, setActionClaim] = useState(null);
  const [actionTab, setActionTab] = useState('resolve'); // 'resolve' | 'reallocate'
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchExceptionsAndSalvagers();

    const handleClickOutside = () => setContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchExceptionsAndSalvagers = async () => {
    setLoading(true);
    try {
      // 1. Fetch claims marked as 'Issue'
      const { data: claimsData, error: claimsError } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .eq('status', 'Issue')
        .order('created_at', { ascending: false });

      if (claimsError) throw claimsError;

      // 2. Fetch Salvager profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles_1783455000000')
        .select('id, company_name')
        .eq('role', 'SALVAGER');

      if (profilesError) throw profilesError;
      setSalvagersList(profilesData || []);

      // 3. Map salvager names to claims
      const profileMap = {};
      profilesData?.forEach(p => {
        profileMap[p.id] = p.company_name;
      });

      const enriched = (claimsData || []).map(c => ({
        ...c,
        salvagerName: profileMap[c.assigned_salvager_id] || 'Unknown Salvager'
      }));

      setExceptions(enriched);
    } catch (err) {
      console.error("Error fetching exceptions:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleContextMenu = (e, claim) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, claim });
  };

  const handleOpenAction = (claim, defaultTab = 'resolve') => {
    setActionClaim(claim);
    setResolutionNote('');
    setActionTab(defaultTab);
    setActionModalOpen(true);
    setContextMenu(null);
  };

  const submitResolution = async () => {
    if (!resolutionNote.trim() || !actionClaim) return;
    setIsSubmitting(true);
    try {
      // 1. Update status back to Accepted so salvager can continue working
      const { error: updateError } = await supabase
        .from('claims_1783447171616')
        .update({ status: 'Accepted' })
        .eq('id', actionClaim.id);

      if (updateError) throw updateError;

      // 2. Insert resolution note explicitly into the Claim Notes so it stays with the claim history
      await supabase
        .from('claim_notes_1783454000000')
        .insert([{
          claim_id: actionClaim.id,
          note_text: `RESOLVED ISSUE: ${resolutionNote}`,
          author: profile?.company_name || 'Insurer Admin'
        }]);

      // 3. Send direct notification to Salvager that their issue is sorted
      if (actionClaim.assigned_salvager_id) {
        await supabase
          .from('notifications_1783875000000')
          .insert([{
            target_user_id: actionClaim.assigned_salvager_id,
            title: 'Claim Issue Sorted',
            message: `Claim ${actionClaim.claim_number} issue has been sorted. It has been moved back to your Accepted queue. Check notes for details.`,
            link: '/my-claims'
          }]);
      }

      await fetchExceptionsAndSalvagers();
      setActionModalOpen(false);
    } catch (err) {
      console.error("Error resolving claim:", err);
      alert("Failed to resolve claim.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitReallocation = async () => {
    if (!actionClaim) return;
    setIsSubmitting(true);
    try {
      // Remove from salvager and return to Global Pool
      const { error: updateError } = await supabase
        .from('claims_1783447171616')
        .update({ status: 'New', assigned_salvager_id: null })
        .eq('id', actionClaim.id);

      if (updateError) throw updateError;

      await supabase
        .from('claim_notes_1783454000000')
        .insert([{
          claim_id: actionClaim.id,
          note_text: `REMOVED FROM SALVAGER: Claim returned to New Claims pool. ${resolutionNote ? 'Reason: ' + resolutionNote : ''}`,
          author: profile?.company_name || 'Insurer Admin'
        }]);

      await fetchExceptionsAndSalvagers();
      setActionModalOpen(false);
    } catch (err) {
      console.error("Error removing claim:", err);
      alert("Failed to remove claim.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredExceptions = exceptions.filter(ex => 
    ex.claim_number.toLowerCase().includes(searchQuery.toLowerCase()) || 
    (ex.ber_reason && ex.ber_reason.toLowerCase().includes(searchQuery.toLowerCase())) ||
    ex.salvagerName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Exceptions Queue</h1>
        <p className="text-gray-500">Manage claims requiring manual intervention to proceed.</p>
      </div>

      <Card className="mb-6 !p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex-1 w-full max-w-md relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search exceptions by claim number or reason..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm"
            />
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><SafeIcon icon={FiMoreVertical} /> Right-click rows for tools</span>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 text-sm font-medium text-gray-700">
              <SafeIcon icon={FiFilter} /> Filter
            </button>
          </div>
        </div>

        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 text-xs uppercase text-gray-400 tracking-wider bg-gray-50/50">
                <th className="px-6 py-4 font-semibold">Claim Number</th>
                <th className="px-6 py-4 font-semibold">Item & Salvager</th>
                <th className="px-6 py-4 font-semibold">Reported Issue</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <SafeIcon icon={FiLoader} className="animate-spin inline-block mr-2" /> Loading exceptions...
                  </td>
                </tr>
              ) : filteredExceptions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <SafeIcon icon={FiCheck} size={48} className="opacity-20 text-green-500" />
                      <p className="italic text-sm">No active exceptions found. Great job!</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredExceptions.map((ex) => (
                  <tr 
                    key={ex.id} 
                    className={`hover:bg-orange-50/30 transition-colors cursor-pointer ${contextMenu?.claim?.id === ex.id ? 'bg-orange-50/50' : ''}`}
                    onClick={() => { setSelectedClaim(ex); setIsDrawerOpen(true); }} // Left-click to view details
                    onContextMenu={(e) => handleContextMenu(e, ex)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-bold text-gray-900">{ex.claim_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{ex.salvage_item || ex.make}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <SafeIcon icon={FiTruck} size={10} /> {ex.salvagerName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-2 text-red-600 font-medium max-w-xs leading-snug">
                        <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
                        <span>{ex.ber_reason}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge status={ex.status}>{ex.status}</Badge>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(ex.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedClaim(ex); setIsDrawerOpen(true); }}
                          className="p-2 text-gray-400 hover:text-brand hover:bg-brand/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <SafeIcon icon={FiEye} />
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleOpenAction(ex); }}
                          className="px-4 py-1.5 bg-orange-500 text-white text-xs rounded-xl font-bold hover:bg-orange-600 transition-colors shadow-sm"
                        >
                          Resolve
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Context Menu */}
      {contextMenu && (
        <div 
          className="fixed bg-white shadow-2xl rounded-xl border border-gray-100 py-2 w-56 z-[100] animate-in fade-in zoom-in duration-100"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-2 border-b border-gray-50 mb-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{contextMenu.claim.claim_number}</p>
          </div>
          
          <button 
            onClick={() => { setSelectedClaim(contextMenu.claim); setIsDrawerOpen(true); setContextMenu(null); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 font-medium"
          >
            <SafeIcon icon={FiEye} className="text-gray-400" /> View Detail & Notes
          </button>
          
          <div className="h-px bg-gray-100 my-1 mx-2" />
          
          <button 
            onClick={() => handleOpenAction(contextMenu.claim, 'resolve')}
            className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-3 font-medium"
          >
            <SafeIcon icon={FiCheckCircle} className="text-green-500" /> Mark as Sorted
          </button>
          
          <button 
            onClick={() => handleOpenAction(contextMenu.claim, 'reallocate')}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium"
          >
            <SafeIcon icon={FiX} className="text-red-500" /> Remove from Salvager
          </button>
        </div>
      )}

      {/* Action / Resolution Modal */}
      {actionModalOpen && actionClaim && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={() => !isSubmitting && setActionModalOpen(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl shadow-2xl z-50 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Handle Exception</h3>
                <p className="text-sm text-gray-500">Claim {actionClaim.claim_number}</p>
              </div>
              <button onClick={() => !isSubmitting && setActionModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-6">
                <p className="text-xs font-bold text-red-800 uppercase tracking-widest mb-1 flex items-center gap-1.5"><SafeIcon icon={FiAlertTriangle} /> Reported Issue</p>
                <p className="text-sm text-red-600 font-medium">{actionClaim.ber_reason}</p>
              </div>

              <div className="flex p-1 bg-gray-100 rounded-xl mb-6">
                <button 
                  onClick={() => setActionTab('resolve')}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${actionTab === 'resolve' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Sorted (Return to Salvager)
                </button>
                <button 
                  onClick={() => setActionTab('reallocate')}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${actionTab === 'reallocate' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Remove Claim
                </button>
              </div>

              {actionTab === 'resolve' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                      <SafeIcon icon={FiMessageSquare} /> Admin Note / Instructions
                    </label>
                    <textarea 
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="Explain how the issue was sorted. This will be added to the claim notes and the claim will be returned to their active queue..."
                      className="w-full p-4 border border-gray-200 bg-gray-50 rounded-2xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none text-sm min-h-[120px] resize-none"
                    />
                  </div>
                  <button 
                    onClick={submitResolution}
                    disabled={isSubmitting || !resolutionNote.trim()}
                    className="w-full py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-brand-light disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />}
                    Mark Sorted & Return to Salvager
                  </button>
                </div>
              )}

              {actionTab === 'reallocate' && (
                <div className="space-y-4 animate-in fade-in slide-in-from-left-4 duration-300">
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-orange-800 text-sm">
                    <strong>Note:</strong> Removing this claim will strip the assigned salvager and return the claim to your "New Claims" pool for redistribution.
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 mt-4">Internal Removal Note (Optional)</label>
                    <textarea 
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="Reason for removal..."
                      className="w-full p-3 border border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none text-sm min-h-[80px] resize-none"
                    />
                  </div>
                  <button 
                    onClick={submitReallocation}
                    disabled={isSubmitting}
                    className="w-full py-3 bg-red-600 text-white rounded-xl font-bold text-sm hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-md mt-2"
                  >
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiRefreshCw} />}
                    Remove from Salvager
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <ClaimDrawer 
        claim={selectedClaim} 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onUpdate={fetchExceptionsAndSalvagers}
      />
    </div>
  );
};